import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MotivationPage from "../../pages/MotivationPage";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  callWithAuth,
  postLessonCompleted,
  postChapterCompleted,
  getPlantStats,
  postCourseCompleted,
  getShouldSeeNPS,
} from "@/api/Api";
import {
  Lesson,
  PostLessonCompletedRequest,
  PostLessonCompletedResponse,
  PostChapterCompletedRequest,
  PostChapterCompletedResponse,
  PostCourseCompletedRequest,
  PostCourseCompletedResponse,
} from "@shared_types";
import { LessonStats } from "@/pages/UseLessonStats";
import SignUpPrompt from "./SignUpPrompt";
import FeedbackPopup from "../FeedbackPopup";
import { LoadingPage } from "@/pages/LoadingPage";
import partyPopperEmoji from "../../assets/emojis/party-popper.webp";

interface ModuleCompleteProps {
  lessonStats: LessonStats;
  isTempUser: boolean;
  chapterId: number;
  lessonId: number;
  isLastLesson: boolean;
  isLastChapter: boolean;
  courseId: number | undefined;
  nextLessonId: number;
  lesson: Lesson;
  onAddDroplets: (droplets: number) => void;
}

const ModuleComplete: React.FC<ModuleCompleteProps> = ({
  isTempUser,
  isLastLesson,
  isLastChapter,
  lessonStats,
  chapterId,
  courseId,
  lessonId,
  lesson,
  nextLessonId,
  onAddDroplets,
}) => {
  const navigate = useNavigate();
  const isLessonCompleteView = !isLastLesson;
  const isChapterCompleteView = isLastLesson;
  const isCourseCompleteView = isLastLesson && isLastChapter;

  const lessonNumber = lesson.sequence + 1;

  // State to track completion API calls
  const [completionCallsDone, setCompletionCallsDone] = useState(false);

  const plantStatsQuery = useQuery({
    queryKey: ["plantStats"],
    queryFn: () => {
      return callWithAuth(getPlantStats);
    },
  });

  const shouldAskForFeedbackQueryKey = ["shouldAskForFeedback"];

  const shouldAskForFeedbackQuery = useQuery({
    queryKey: shouldAskForFeedbackQueryKey,
    queryFn: () => {
      return callWithAuth(getShouldSeeNPS);
    },
  });

  const queryClient = useQueryClient();

  // Check if all necessary data is loaded
  const isDataLoading = plantStatsQuery.isLoading || shouldAskForFeedbackQuery.isLoading || !completionCallsDone;

  const plantStats = plantStatsQuery.data || {
    waterDropletsCount: 0,
    waterDropletsNeeded: 0,
    chaptersCompleted: 0,
    treesPlanted: 0,
    co2Captured: 0.0,
  };

  const plantSizePercentage =
    plantStats.waterDropletsCount / plantStats.waterDropletsNeeded;

  const previousDropletsCount =
    plantStats.waterDropletsCount - lessonStats.dropsAdded;
  const previousPlantSizePercentage =
    previousDropletsCount / plantStats.waterDropletsNeeded;

  let isPlantingTree =
    plantStats.waterDropletsCount >= plantStats.waterDropletsNeeded;

  const TREE_GROWTH_THRESHOLD = 6; // scaling factor for plant size based on 6 animations we have

  let treeIncrease =
    Math.floor(plantSizePercentage * TREE_GROWTH_THRESHOLD) >
      Math.floor(previousPlantSizePercentage * TREE_GROWTH_THRESHOLD) ||
    isPlantingTree;

  const handleNavigateToNextModule = async () => {
    // to next lesson
    if (isLessonCompleteView) {
      navigate(`/chapter/${chapterId}/lesson/${nextLessonId}`);
      return;
    }
    if (isChapterCompleteView || isCourseCompleteView) {
      navigate(`/home/learn`);
    }
  };

  if (plantStatsQuery.isLoading) {
    treeIncrease = false;
    isPlantingTree = false;
  }

  // State to control feedback popup with delay
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);

  // Effect to add delay before showing the feedback popup
  useEffect(() => {
    if (
      shouldAskForFeedbackQuery.data !== undefined &&
      shouldAskForFeedbackQuery.data?.shouldSeeNPS
    ) {
      const delayMS = 650;
      const timer = setTimeout(() => {
        setShowFeedbackPopup(true);
      }, delayMS);

      return () => clearTimeout(timer);
    }
  }, [shouldAskForFeedbackQuery.data]);

  useEffect(() => {
    if (lessonId) {
      const completionPromises: Promise<void>[] = [];
      
      // Lesson completion
      const lessonData: PostLessonCompletedRequest = {
        lessonId: lessonId,
      };
      completionPromises.push(
        callWithAuth(postLessonCompleted, lessonData).then(
          (res: PostLessonCompletedResponse) => {
            onAddDroplets(res.waterDropletsAwarded);
          }
        )
      );

      // Chapter completion
      if (isChapterCompleteView) {
        const chapterData: PostChapterCompletedRequest = {
          chapterId: chapterId,
        };
        completionPromises.push(
          callWithAuth(postChapterCompleted, chapterData).then(
            (res: PostChapterCompletedResponse) => {
              onAddDroplets(res.waterDropletsAwarded);
            }
          )
        );
      }

      // Course completion
      if (isCourseCompleteView && courseId) {
        const courseData: PostCourseCompletedRequest = {
          courseId: courseId,
        };
        completionPromises.push(
          callWithAuth(postCourseCompleted, courseData).then(
            (res: PostCourseCompletedResponse) => {
              onAddDroplets(res.waterDropletsAwarded);
            }
          )
        );
      }

      // Wait for all completion calls to finish
      Promise.all(completionPromises).then(() => {
        setCompletionCallsDone(true);
      });
    }
  }, [lessonId]);

  // Show loading state while data is being fetched
  if (isDataLoading) {
    return (
      <LoadingPage
        className="min-h-screen"
        showText={true}
      />
    );
  }

  if (lessonNumber === 1 && isTempUser) {
    return (
      <SignUpPrompt
        buttonAction={() => navigate("/signup")}
        lessonStats={lessonStats}
      />
    );
  }

  if (isCourseCompleteView) {
    return (
      <MotivationPage
        lessonStats={lessonStats}
        title={
          <>
            Course <span className="text-[var(--color-green)]">Completed</span>
            <br />
            <div className="text-center">
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
            </div>
          </>
        }
        subtitle={
          <span className="text-center items-center justify-center">
            Fantastic! Keep up the great work with Parrot and you could be
            fluent in no time!
          </span>
        }
        treeIncrease={treeIncrease}
        isPlantingTree={isPlantingTree}
        plantStats={plantStats}
        buttonText="Back to home"
        buttonAction={() => navigate("/home/learn")}
      />
    );
  }

  if (isChapterCompleteView) {
    return (
      <MotivationPage
        lessonStats={lessonStats}
        title={
          <>
            {"Chapter"}{" "}
            <span className="text-[var(--color-green)]">Completed</span>
            <br />
            <div className="text-center">
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
            </div>
          </>
        }
        subtitle={
          <span className="text-center items-center justify-center">
            Fantastic! Keep up the great work with Parrot and you could be
            fluent in no time!
          </span>
        }
        treeIncrease={treeIncrease}
        isPlantingTree={isPlantingTree}
        plantStats={plantStats}
        buttonText="Back to home"
        buttonAction={() => navigate("/home/learn")}
      />
    );
  }

  return (
    <>
      <MotivationPage
        lessonStats={lessonStats}
        title={
          <>
            Lesson {lessonNumber}{" "}
            <span className="text-[var(--color-green)]">Completed</span>
            <br />
            <div className="text-center">
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
              <img src={partyPopperEmoji} alt="Party Popper" width="40" height="40" className="inline-block" />
            </div>
          </>
        }
        subtitle={
          <span className="text-center items-center justify-center">
            Great job! Keep up the great work with Parrot and you could be
            fluent in no time!
          </span>
        }
        treeIncrease={treeIncrease}
        isPlantingTree={isPlantingTree}
        plantStats={plantStats}
        buttonText="Continue to next lesson"
        buttonAction={handleNavigateToNextModule}
        repeatButtonAction={() => {
          navigate(`/chapter/${chapterId}/lesson/${lessonId}`);
        }}
      />
      <FeedbackPopup
        isOpen={showFeedbackPopup}
        onClose={() => {
          setShowFeedbackPopup(false);
          queryClient.setQueryData(shouldAskForFeedbackQueryKey, {
            isComplete: true,
          });
        }}
      />
    </>
  );
};

export default ModuleComplete;
